
import { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import type { FinanceRecord } from '../../../store/financeStore';

const MONTHS = [
  '1月',
  '2月',
  '3月',
  '4月',
  '5月',
  '6月',
  '7月',
  '8月',
  '9月',
  '10月',
  '11月',
  '12月',
];

const VARIETY_COLORS: Record<string, string> = {
  小麦: '#0d9488',
  玉米: '#f59e0b',
  大米: '#10b981',
  大豆: '#ef4444',
  高粱: '#8b5cf6',
  稻谷: '#06b6d4',
};

interface Props {
  records: FinanceRecord[];
}

type ViewMode = 'variety' | 'monthly';

export default function ProfitAnalysisChart({ records }: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('variety');
  const [year] = useState('2026');

  const yearRecords = useMemo(
    () => records.filter((r) => r.date.startsWith(year)),
    [records, year]
  );

  // 按品种利润数据
  const varietyData = useMemo(() => {
    const map: Record<
      string,
      {
        revenue: number;
        cost: number;
        grossProfit: number;
        tax: number;
        netProfit: number;
        qty: number;
      }
    > = {};
    yearRecords.forEach((r) => {
      if (!map[r.variety]) {
        map[r.variety] = {
          revenue: 0,
          cost: 0,
          grossProfit: 0,
          tax: 0,
          netProfit: 0,
          qty: 0,
        };
      }
      const v = map[r.variety];
      if (r.bizType === '销售') {
        v.revenue += r.revenue || r.amount;
        v.grossProfit += r.grossProfit;
        v.tax += r.tax;
      }
      v.cost += r.cost || r.operatingCost || 0;
      v.qty += r.quantity;
    });

    return Object.entries(map).map(([variety, data]) => ({
      name: variety,
      营业收入: Math.round(data.revenue / 10000),
      营业成本: Math.round(data.cost / 10000),
      毛利润: Math.round(data.grossProfit / 10000),
      税款: Math.round(data.tax / 10000),
      利润率:
        data.revenue > 0
          ? parseFloat(((data.grossProfit / data.revenue) * 100).toFixed(1))
          : 0,
      color: VARIETY_COLORS[variety] || '#6b7280',
    }));
  }, [yearRecords]);

  // 按月度利润数据
  const monthlyData = useMemo(() => {
    const monthMap: Record<
      number,
      { revenue: number; cost: number; grossProfit: number; tax: number }
    > = {};
    for (let m = 1; m <= 12; m++) {
      monthMap[m] = { revenue: 0, cost: 0, grossProfit: 0, tax: 0 };
    }
    yearRecords.forEach((r) => {
      const month = parseInt(r.date.split('-')[1], 10);
      if (!monthMap[month]) return;
      if (r.bizType === '销售') {
        monthMap[month].revenue += r.revenue || r.amount;
        monthMap[month].grossProfit += r.grossProfit;
        monthMap[month].tax += r.tax;
      }
      monthMap[month].cost += r.cost || r.operatingCost || 0;
    });

    return MONTHS.map((label, idx) => {
      const m = idx + 1;
      const d = monthMap[m];
      return {
        name: label,
        营业收入: Math.round(d.revenue / 10000),
        营业成本: Math.round(d.cost / 10000),
        毛利润: Math.round(d.grossProfit / 10000),
        税款: Math.round(d.tax / 10000),
        利润率:
          d.revenue > 0
            ? parseFloat(((d.grossProfit / d.revenue) * 100).toFixed(1))
            : 0,
      };
    });
  }, [yearRecords]);

  const chartData = viewMode === 'variety' ? varietyData : monthlyData;
  const hasData = chartData.some(
    (d) => d.营业收入 > 0 || d.毛利润 > 0
  );

  // 汇总
  const totals = useMemo(() => {
    return chartData.reduce(
      (acc, d) => ({
        revenue: acc.revenue + d.营业收入,
        cost: acc.cost + d.营业成本,
        profit: acc.profit + d.毛利润,
        tax: acc.tax + d.税款,
      }),
      { revenue: 0, cost: 0, profit: 0, tax: 0 }
    );
  }, [chartData]);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <i className="ri-funds-line text-lg"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">利润分析</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              按品种/月度对比利润数据
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* 汇总指标 */}
          <div className="hidden lg:flex items-center gap-4 mr-4">
            {[
              {
                label: '总收入',
                value: `${totals.revenue}万`,
                color: 'text-teal-600',
              },
              {
                label: '总利润',
                value: `${totals.profit}万`,
                color: 'text-emerald-600',
              },
              {
                label: '利润率',
                value:
                  totals.revenue > 0
                    ? `${((totals.profit / totals.revenue) * 100).toFixed(
                        1
                      )}%`
                    : '0%',
                color: 'text-amber-600',
              },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="text-xs text-gray-400">{item.label}</div>
                <div className={`text-sm font-bold ${item.color}`}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
          {/* 切换按钮 */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            {[
              { key: 'variety' as ViewMode, label: '按品种', icon: 'ri-plant-line' },
              { key: 'monthly' as ViewMode, label: '按月度', icon: 'ri-calendar-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setViewMode(tab.key)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === tab.key
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6">
        {!hasData ? (
          <div className="h-[320px] flex flex-col items-center justify-center text-gray-300 bg-gray-50 rounded-xl">
            <i className="ri-bar-chart-2-line text-4xl mb-3"></i>
            <p className="text-sm text-gray-400">暂无利润数据</p>
            <p className="text-xs text-gray-300 mt-1">
              录入销售数据后将自动生成利润分析图表
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 图表 */}
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v: number) => `${v}万`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      fontSize: '13px',
                      padding: '12px 16px',
                    }}
                    formatter={(value: number, name: string) => [
                      `¥${value.toLocaleString()}万`,
                      name,
                    ]}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    iconType="rect"
                    iconSize={10}
                    wrapperStyle={{
                      fontSize: '12px',
                      color: '#6b7280',
                    }}
                  />
                  <Bar dataKey="营业收入" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`revenue-${index}`}
                        fill={
                          viewMode === 'variety'
                            ? (entry as any).color || '#0d9488'
                            : '#0d9488'
                        }
                        fillOpacity={0.7}
                      />
                    ))}
                  </Bar>
                  <Bar dataKey="毛利润" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`profit-${index}`}
                        fill={
                          viewMode === 'variety'
                            ? (entry as any).color || '#10b981'
                            : '#10b981'
                        }
                        fillOpacity={1}
                      />
                    ))}
                  </Bar>
                  <Bar
                    dataKey="税款"
                    fill="#f59e0b"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    fillOpacity={0.8}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* 利润率对比条 */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-percent-line text-sm text-amber-500"></i>
                <span className="text-xs font-semibold text-gray-600">
                  {viewMode === 'variety' ? '各品种' : '各月度'}利润率对比
                </span>
              </div>
              <div className="space-y-2">
                {chartData
                  .filter((d) => d.营业收入 > 0)
                  .map((d, idx) => {
                    const maxRate = Math.max(
                      ...chartData
                        .filter((x) => x.营业收入 > 0)
                        .map((x) => Math.abs(x.利润率)),
                      1
                    );
                    const barWidth = Math.abs(d.利润率) / maxRate * 100;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-12 text-right shrink-0">
                          {d.name}
                        </span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              d.利润率 >= 0
                                ? 'bg-emerald-500'
                                : 'bg-rose-500'
                            }`}
                            style={{ width: `${Math.min(barWidth, 100)}%` }}
                          ></div>
                        </div>
                        <span
                          className={`text-xs font-bold w-14 text-right shrink-0 ${
                            d.利润率 >= 0
                              ? 'text-emerald-600'
                              : 'text-rose-600'
                          }`}
                        >
                          {d.利润率}%
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
